// Data catalog for the "Umzug" (moving) wizard: Gesamte Wohnung / Gesamtes
// Haus / Einzelne Gegenstände. Distinct from furnitureWizardConfig.js —
// this one intentionally has a smaller/simpler item+size catalog, matching
// the exact "Umzug" spec (separate from the older, more detailed "Möbel /
// Einzelne Gegenstände" spec that furnitureWizardConfig.js implements).

export const MOVE_SUB_TYPES = ["Gesamte Wohnung", "Gesamtes Haus", "Einzelne Gegenstände"];

export const SIZE_SML = ["Klein", "Mittel", "Groß"];

const item = (key, opts = {}) => ({ key, quantityDefault: 0, ...opts });

// Items whose key appears here are offered in the "Ab- und Aufbauservice"
// step — exactly the curated list from the spec (NOT every item, e.g. no
// sofa/washing machine/fridge).
export const ASSEMBLY_ELIGIBLE_KEYS = [
  "Kleiderschrank",
  "Bett",
  "Kinderbett",
  "Wohnwand",
  "Regal",
  "Esstisch",
  "Schreibtisch",
  "Kommode",
  "Vitrine",
  "Sideboard",
  "Badezimmerschrank",
  "Gartenschrank",
];

// "Küche" isn't a room item on its own — it's a virtual assembly-step entry
// that appears whenever ANY kitchen item was selected in the Küche room.
export const KITCHEN_ROOM_KEY = "Küche";

export const MOVE_AREAS = [
  {
    key: "Wohnzimmer",
    items: [
      item("Sofa", { typeOptions: ["2-Sitzer", "3-Sitzer", "4-Sitzer", "Ecksofa", "Schlafsofa", "Wohnlandschaft"] }),
      item("Sessel"),
      item("Couchtisch", { sizeOptions: SIZE_SML }),
      item("Fernseher", { sizeOptions: ["Bis 55 Zoll", "56–75 Zoll", "Über 75 Zoll"] }),
      item("TV-Schrank / Lowboard", { sizeOptions: SIZE_SML }),
      item("Wohnwand", { sizeOptions: SIZE_SML }),
      item("Regal", { sizeOptions: SIZE_SML }),
      item("Vitrine", { sizeOptions: SIZE_SML }),
      item("Kommode", { sizeOptions: SIZE_SML }),
      item("Stehlampe"),
      item("Teppich", { sizeOptions: SIZE_SML }),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Schlafzimmer",
    items: [
      item("Bett", { typeOptions: ["Einzelbett", "Doppelbett", "Boxspringbett", "Etagenbett"] }),
      item("Matratze", { typeOptions: ["Einzelmatratze", "Doppelmatratze"] }),
      item("Kleiderschrank", { sizeOptions: ["1–2 Türen", "3 Türen", "4 Türen", "5 oder mehr Türen", "Schwebetürenschrank"] }),
      item("Nachttisch"),
      item("Kommode", { sizeOptions: SIZE_SML }),
      item("Schminktisch", { sizeOptions: SIZE_SML }),
      item("Spiegel", { sizeOptions: SIZE_SML }),
      item("Fernseher", { sizeOptions: ["Bis 55 Zoll", "56–75 Zoll", "Über 75 Zoll"] }),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Kinderzimmer",
    items: [
      item("Kinderbett", { typeOptions: ["Babybett", "Einzelbett", "Etagenbett"] }),
      item("Kleiderschrank", { sizeOptions: ["1–2 Türen", "3 Türen", "4 oder mehr Türen"] }),
      item("Wickelkommode"),
      item("Kommode", { sizeOptions: SIZE_SML }),
      item("Schreibtisch", { sizeOptions: SIZE_SML }),
      item("Schreibtischstuhl"),
      item("Regal", { sizeOptions: SIZE_SML }),
      item("Spielzeugkiste"),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Küche",
    items: [
      item("Kühlschrank", { typeOptions: ["Kleiner Kühlschrank", "Kühlschrank", "Kühl-Gefrierkombination", "Side-by-Side-Kühlschrank"] }),
      item("Gefrierschrank / Gefriertruhe", { sizeOptions: SIZE_SML }),
      item("Waschmaschine"),
      item("Trockner"),
      item("Geschirrspüler", { sizeOptions: ["45 cm", "60 cm"] }),
      item("Herd / Backofen", { typeOptions: ["Einbaugerät", "Freistehendes Gerät"] }),
      item("Mikrowelle"),
      item("Küchenmaschine"),
      item("Küchenschrank", { typeOptions: ["Unterschrank", "Hängeschrank", "Hochschrank"] }),
      item("Esstisch", { sizeOptions: ["Bis 4 Personen", "4–6 Personen", "Mehr als 6 Personen"] }),
      item("Stuhl"),
      item("Küchenbank", { sizeOptions: SIZE_SML }),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Esszimmer",
    items: [
      item("Esstisch", { sizeOptions: ["Bis 4 Personen", "4–6 Personen", "Mehr als 6 Personen"] }),
      item("Stuhl"),
      item("Esszimmerbank", { sizeOptions: SIZE_SML }),
      item("Vitrine", { sizeOptions: SIZE_SML }),
      item("Sideboard", { sizeOptions: SIZE_SML }),
      item("Regal", { sizeOptions: SIZE_SML }),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Arbeitszimmer / Büro",
    items: [
      item("Schreibtisch", { typeOptions: ["Klein", "Mittel", "Groß", "Eckschreibtisch"] }),
      item("Bürostuhl"),
      item("Aktenschrank", { sizeOptions: SIZE_SML }),
      item("Regal", { sizeOptions: SIZE_SML }),
      item("Computer"),
      item("Monitor", { sizeOptions: ["Bis 27 Zoll", "28–40 Zoll", "Über 40 Zoll"] }),
      item("Drucker", { typeOptions: ["Kleiner Drucker", "Großer Drucker / Multifunktionsgerät"] }),
      item("Sofa", { typeOptions: ["2-Sitzer", "3-Sitzer", "Schlafsofa"] }),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Badezimmer",
    items: [
      item("Waschmaschine"),
      item("Trockner"),
      item("Badezimmerschrank", { sizeOptions: SIZE_SML }),
      item("Spiegelschrank", { sizeOptions: SIZE_SML }),
      item("Regal"),
      item("Wäschekorb"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Flur",
    items: [
      item("Garderobe", { sizeOptions: SIZE_SML }),
      item("Schuhschrank", { sizeOptions: SIZE_SML }),
      item("Kommode", { sizeOptions: SIZE_SML }),
      item("Spiegel", { sizeOptions: SIZE_SML }),
      item("Bank"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Keller",
    items: [
      item("Regal", { sizeOptions: SIZE_SML }),
      item("Schwerlastregal", { sizeOptions: SIZE_SML }),
      item("Werkzeugschrank", { sizeOptions: SIZE_SML }),
      item("Werkbank", { sizeOptions: SIZE_SML }),
      item("Fahrrad"),
      item("E-Bike"),
      item("Reifen"),
      item("Koffer"),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Dachboden",
    items: [
      item("Umzugskarton"),
      item("Koffer"),
      item("Regal", { sizeOptions: SIZE_SML }),
      item("Schrank", { sizeOptions: SIZE_SML }),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Garage",
    items: [
      item("Fahrrad"),
      item("E-Bike"),
      item("Motorrad", { specialTransport: true }), // spec: registers internally as special transport
      item("Reifen"),
      item("Werkzeugschrank", { sizeOptions: SIZE_SML }),
      item("Werkbank", { sizeOptions: SIZE_SML }),
      item("Regal", { sizeOptions: SIZE_SML }),
      item("Schwerlastregal", { sizeOptions: SIZE_SML }),
      item("Gartengeräte"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Balkon / Terrasse",
    items: [
      item("Gartentisch", { sizeOptions: SIZE_SML }),
      item("Gartenstuhl"),
      item("Gartenbank", { sizeOptions: SIZE_SML }),
      item("Grill", { typeOptions: ["Kleiner Grill", "Gasgrill", "Großer Grill"] }),
      item("Sonnenschirm"),
      item("Pflanze", { sizeOptions: SIZE_SML }),
      item("Pflanzkübel", { sizeOptions: SIZE_SML }),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Sonstiger Raum",
    isCustomArea: true,
    items: [],
  },
];

export const MOVE_HEAVY_ITEMS = [
  item("Klavier", { typeOptions: ["Klavier", "Flügel", "E-Piano"] }),
  item("Tresor", { sizeOptions: ["Bis 100 kg", "100–200 kg", "Über 200 kg", "Nicht bekannt"], sizeLabel: "weight" }),
  item("Aquarium", { sizeOptions: ["Bis 100 Liter", "101–300 Liter", "Über 300 Liter"] }),
  item("Billardtisch"),
  item("Fitnessgerät", { typeOptions: ["Laufband", "Crosstrainer", "Heimtrainer", "Kraftstation", "Sonstiges"] }),
  item("Massivholzmöbel", { sizeOptions: SIZE_SML }),
  item("Kunstwerk / großes Bild", { sizeOptions: SIZE_SML }),
  item("Sonstiger schwerer Gegenstand", {
    requiresDescription: true,
    sizeOptions: ["Bis 50 kg", "51–100 kg", "101–200 kg", "Über 200 kg", "Nicht bekannt"],
    sizeLabel: "weight",
  }),
];

export const ASSEMBLY_SERVICE_OPTIONS = ["Ja", "Nein", "Ich bin nicht sicher"];
export const ASSEMBLY_LEVEL_OPTIONS = ["Nur Abbau", "Nur Aufbau", "Abbau und Aufbau"];
export const KITCHEN_SERVICE_OPTIONS = ["Nur Demontage", "Nur Montage", "Demontage und Montage", "Ich benötige eine Beratung"];